import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { db } from '../firebase/client';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useGeolocation } from '../hooks/useGeolocation';
import { MapPin, Store, User as UserIcon, Phone, Map, Globe, AlertCircle, CheckCircle2 } from 'lucide-react';

const RestaurateurOnboardingScreen: React.FC = () => {
    const { currentUser, refreshProfile, updateUserProfile } = useAuth();
    const { wilayas } = useData();
    const [restaurantName, setRestaurantName] = useState('');
    const [ownerName, setOwnerName] = useState(currentUser?.name || '');
    const [phone, setPhone] = useState(currentUser?.phone || '');
    const [wilaya, setWilaya] = useState(currentUser?.wilaya || '');
    const [restaurantLocation, setRestaurantLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { getCurrentPosition } = useGeolocation();
    const [isLocating, setIsLocating] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        if (currentUser) {
            setOwnerName(currentUser.name || '');
            setPhone(currentUser.phone || '');
            setWilaya(currentUser.wilaya || '');
        }
    }, [currentUser]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        if (!restaurantName.trim() || !ownerName.trim() || !phone.trim() || !wilaya) {
            setError("Veuillez remplir tous les champs obligatoires.");
            setIsLoading(false);
            return;
        }

        try {
            const userId = currentUser!.uid;
            let restaurantId = currentUser?.restaurantId;

            if (!restaurantId) {
                restaurantId = `resto_${Date.now()}`;
            }

            // 1. UPDATE or CREATE restaurant in Firestore
            const trialExpiry = new Date();
            trialExpiry.setDate(trialExpiry.getDate() + 7);

            await setDoc(doc(db, 'restaurants', restaurantId), {
                id: restaurantId,
                ownerId: userId,
                ownerName: ownerName.trim(), // Added ownerName
                name: restaurantName.trim(),
                phone: phone.trim(),
                wilaya: wilaya,
                status: 'pending',
                rating: 0,
                ratingCount: 0,
                image: "https://images.unsplash.com/photo-1614332287897-cdc485fa562d?q=80&w=2070&auto=format&fit=crop",
                gallery: [],
                deliveryEnabled: false,
                deliveryPrice: 0,
                location: restaurantLocation,
                globalDiscount: 0,
                subscriptionType: 'trial',
                subscriptionStatus: 'trial',
                trialExpiry: trialExpiry,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            }, { merge: true });

            // 2. Update Profile in Firestore
            await updateUserProfile({
                name: ownerName.trim(),
                restaurantName: restaurantName.trim(),
                restaurantId: restaurantId,
                restaurantStatus: 'pending',
                phone: phone.trim(),
                wilaya: wilaya
            });

            // 3. Show success and refresh context
            setShowSuccess(true);
            setTimeout(async () => {
                await refreshProfile();
            }, 3000);

        } catch (err: any) {
            console.error("Onboarding Error:", err);
            setError("Erreur lors de la configuration : " + err.message);
            setIsLoading(false);
        }
    };

    if (showSuccess) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 max-w-sm w-full text-center animate-pop-in border border-gray-100">
                    <div className="mx-auto w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6 shadow-sm">
                        <CheckCircle2 className="w-10 h-10 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 mb-4 uppercase italic tracking-tighter">Demande Envoyée !</h2>
                    <p className="text-gray-600 mb-8 font-medium leading-relaxed">
                        Vos informations ont été enregistrées avec succès. Nos administrateurs vont valider votre compte sous peu pour que vous puissiez commencer à vendre sur <span className="text-red-600 font-black">Yumii</span>.
                    </p>
                    <div className="bg-red-50 rounded-2xl p-4 text-xs font-black text-red-600 uppercase tracking-widest italic animate-pulse">
                        Redirection vers votre espace...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20 flex flex-col">
            {/* Header Area */}
            <div className="pt-12 px-6 pb-4">
                <div className="max-w-md mx-auto text-center">
                    <div className="h-14 flex justify-center mb-6">
                        <img src="/logo-red.svg" alt="Yumii" className="h-full w-auto object-contain" />
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 mb-1 uppercase italic tracking-tighter">Étape Finale</h1>
                    <p className="text-gray-500 text-sm font-medium">Complétez votre profil professionnel pour commencer</p>
                </div>
            </div>

            <div className="max-w-md mx-auto px-6 w-full">
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 p-8 border border-gray-100 animate-fade-in-up">
                    <form onSubmit={handleSubmit} className="space-y-5 pb-4 text-left">
                        
                        {/* 1. Nom et Prénom */}
                        <div className="animate-fade-in">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Nom et prénom</label>
                            <div className="relative">
                                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                                <input
                                    type="text"
                                    value={ownerName}
                                    onChange={(e) => {setOwnerName(e.target.value); setError('');}}
                                    placeholder="Ex: Amine Alje"
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                    required
                                />
                            </div>
                        </div>

                        {/* 2. Nom du Restaurant */}
                        <div className="animate-fade-in">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Nom du Restaurant</label>
                            <div className="relative">
                                <Store className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                                <input
                                    type="text"
                                    value={restaurantName}
                                    onChange={(e) => {setRestaurantName(e.target.value); setError('');}}
                                    placeholder="Ex: Le Gourmet d'Alger"
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                    required
                                />
                            </div>
                        </div>

                        {/* 3. Email (Pre-filled & Disabled) */}
                        <div className="animate-fade-in">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Email</label>
                            <div className="relative">
                                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                                <input
                                    type="email"
                                    value={currentUser?.email || ''}
                                    disabled
                                    className="w-full pl-12 pr-4 py-4 bg-gray-100 border border-gray-100 rounded-2xl text-gray-400 font-medium cursor-not-allowed"
                                />
                            </div>
                        </div>

                        {/* 4. Wilaya (Select) */}
                        <div className="animate-fade-in">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Wilaya</label>
                            <div className="relative">
                                <Map className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                                <select
                                    value={wilaya}
                                    onChange={(e) => {setWilaya(e.target.value); setError('');}}
                                    required
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium appearance-none"
                                >
                                    <option value="" disabled>Sélectionnez votre Wilaya</option>
                                    {wilayas.map(w => <option key={w} value={w}>{w}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* 5. Phone Input */}
                        <div className="animate-fade-in">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Num tel</label>
                            <div className="relative">
                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" />
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setPhone(val);
                                        setError('');
                                    }}
                                    maxLength={10}
                                    placeholder="05 / 06 / 07 ..."
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                    required
                                />
                            </div>
                        </div>

                        {/* Location Picker Section */}
                        <div className="pt-2 animate-fade-in">
                            <button
                                type="button"
                                onClick={async () => {
                                    setIsLocating(true);
                                    try {
                                        const pos = await getCurrentPosition();
                                        if (pos) {
                                            setRestaurantLocation({ lat: pos.lat, lng: pos.lng });
                                        }
                                    } finally {
                                        setIsLocating(false);
                                    }
                                }}
                                className={`w-full flex items-center justify-center gap-3 py-4 border-2 rounded-2xl font-black text-xs uppercase tracking-widest italic transition-all ${restaurantLocation ? 'bg-green-50 border-green-200 text-green-700 shadow-sm' : 'bg-gray-50 border-gray-100 text-gray-500 hover:bg-gray-100'}`}
                            >
                                <MapPin className={`w-5 h-5 ${isLocating ? 'animate-bounce' : ''}`} />
                                {isLocating ? 'Localisation...' : restaurantLocation ? 'Position validée ✓' : 'Ma Position GPS'}
                            </button>
                            <p className="text-[10px] text-gray-400 mt-2 px-1 font-bold uppercase tracking-tighter text-center">
                                Utilisé pour situer votre restaurant sur la carte et calculer la livraison.
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center gap-3 animate-shake">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                                <p className="text-sm font-bold text-red-800">{error}</p>
                            </div>
                        )}

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full py-5 bg-red-600 hover:bg-red-700 text-white font-black rounded-[1.5rem] shadow-xl shadow-red-200 transition-all active:scale-[0.98] disabled:bg-gray-300 disabled:shadow-none text-lg flex items-center justify-center gap-3 uppercase italic tracking-tighter"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        <span>Patientez...</span>
                                    </>
                                ) : 'Soumettre ma demande'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RestaurateurOnboardingScreen;
